/**
 * Talking to the panel's API.
 *
 * Every call goes through `request`, for three reasons that each showed up as
 * a bug first: a session that expired mid-edit has to send the person back to
 * the sign-in screen rather than showing an empty list; a refusal has to arrive
 * as a sentence somebody can read; and a 422 carrying per-field findings is not
 * an error to throw but an answer to render on the form.
 */

export class ApiError extends Error {
  /**
   * @param {string} message
   * @param {{status?: number, findings?: object[]|null, data?: object|null}} [detail]
   */
  constructor(message, detail = {}) {
    const { status, findings = null, data = null } = detail;
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.findings = findings;
    /** Whatever else the refusal carried — a suggested address, say. */
    this.data = data;
  }
}

/** Set by the app so an expired session can send everyone back to sign in. */
let onSignedOut = () => {};
export const whenSignedOut = (fn) => (onSignedOut = fn);

async function request(method, path, body, { raw = false } = {}) {
  /** @type {RequestInit} */
  const init = { method, credentials: "same-origin", headers: {} };
  if (body instanceof FormData) init.body = body;
  else if (body !== undefined) {
    init.body = JSON.stringify(body);
    init.headers["content-type"] = "application/json";
  }

  let response;
  try {
    response = await fetch(`/api/${path}`, init);
  } catch {
    throw new ApiError("The panel could not reach its server. Check your connection and try again.", { status: 0 });
  }

  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new ApiError("The panel got an answer it could not read.", { status: response.status });
  }

  if (response.status === 401 && !path.startsWith("auth/")) {
    onSignedOut();
    throw new ApiError("Your session has ended. Sign in again.", { status: 401 });
  }
  if (response.status === 422) {
    throw new ApiError("Some fields need attention before this can be saved.", {
      status: 422,
      findings: data?.findings || [],
    });
  }
  if (!response.ok) {
    throw new ApiError(data?.error || `The panel could not do that (${response.status}).`, { status: response.status, data });
  }
  return raw ? text : data;
}

export const api = {
  get: (path) => request("GET", path),
  post: (path, body) => request("POST", path, body),
  put: (path, body) => request("PUT", path, body),
  patch: (path, body) => request("PATCH", path, body),
  del: (path) => request("DELETE", path),
};
